import { useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  canTrainOneSide,
  draftRoundTypes,
  formatDraftValue,
  MAX_CYCLE_WEEKS,
  loadStepFor,
  referenceLoadable,
  roundReferenceKg,
  type CurrentMax,
  type SlotRef,
  type Draft,
  type DraftLineOptions,
  type UnitsPreference,
  dayLabel,
} from "@heraion/shared";

import { IconButton } from "../primitives/IconButton";
import { SideChoiceRow, useSideWord } from "../primitives/SideChoiceRow";
import { PrescribeNoteEditor } from "../ProgramComposer/prescribe/PrescribeNoteEditor";
import type { UiTheme } from "../theme";
import { CopyToDaysTray } from "./CopyToDaysTray";
import { groupLabels } from "./groupLabels";
import { ExerciseSheets } from "./ExerciseSheets";
import { RoundLadder } from "./RoundLadder";
import { RuleCard } from "./RuleCard";
import { ruleOfSlot } from "./ruleCache";
import type { RuleWords } from "./ruleWords";
import { NEXT_BLOCK_ROUNDS, type V3ProgressionActions } from "./useV3ProgressionActions";
import type { V3ExerciseActions } from "./useV3ExerciseActions";
import type { V3Navigation } from "./useV3Navigation";
import type { ProgramEditorV3Props } from "./types";
import type { V3Styles } from "./v3Styles";

// One exercise of the open day (#336): its head with back, the «?» and the
// exercise menu, the note, then the round ladder (the schema line over every
// round is the day's row, not repeated here, #358). Variants are shown, not
// edited; the save keeps them as they are. The «?» is the logger's tip card
// (PE-33); an alternative is a replace. A superset join with uneven set counts
// asks the logger's pad-or-trim question. «Copy to other days» opens its tray
// under the head (2B). Slice 5: the rule card above the ladder, the cells by
// hand marked round by round, and a typed cell that leaves the rule (or comes
// back) said in a toast, and beside a round at effort or at % its reference
// load from the MAX, read only. Without `edit` (#358) it is read only: no «?»,
// menu or typing. #75: the side the coach prescribes, on an exercise the
// catalog lets train one side (the logger's UNI/BI rule) or one with a side.

/** What the «?» card reads, as the host hands it to the editor. */
export type V3TipSeams = Pick<
  ProgramEditorV3Props,
  "exerciseCatalog" | "profile" | "animationSource" | "onAnimationLoadError"
>;

/** What the card writes through, in the editor. */
export type ExerciseEdit = {
  actions: V3ExerciseActions;
  onReplace: () => void;
  tip: V3TipSeams;
  progression: V3ProgressionActions;
  onOpenRule: (refs: SlotRef[]) => void;
  apiFetch?: ProgramEditorV3Props["apiFetch"];
};

export function ExerciseView({
  theme,
  styles,
  draft,
  nav,
  slotIdx,
  edit,
  lockedThrough = -1,
  lineOptions,
  cellUnit,
  resolveName,
  words,
  maxMeta,
  inPane = false,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  nav: V3Navigation;
  slotIdx: number;
  /** Absent: read only. */
  edit?: ExerciseEdit;
  /** Rounds 0..lockedThrough were executed. */
  lockedThrough?: number;
  lineOptions: DraftLineOptions;
  cellUnit: UnitsPreference;
  resolveName: (id: string, snapshot?: string) => string;
  words: RuleWords;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  /** A wide screen's right pane (slice 6): the day stays in view, no back. */
  inPane?: boolean;
}) {
  const { t } = useTranslation();
  const sideWord = useSideWord();
  const [menuOpen, setMenuOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const sIdx = nav.dayIdx;
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const slot = slots[slotIdx];
  if (!slot) return null;
  const types = draftRoundTypes(draft);
  const rec = ruleOfSlot(slot, types, loadStepFor(cellUnit));
  const hand = new Set(rec.kind === "rule" ? rec.exceptions.map((e) => e.week) : []);
  const max = maxMeta?.get(slot.exerciseId);
  const refs = max
    ? Array.from({ length: draft.cycleWeeks }, (_, w) => {
        const kg = roundReferenceKg(slot, w, max);
        if (kg === null) return null;
        const load = formatDraftValue("absolute", referenceLoadable(kg, cellUnit), lineOptions);
        return t("editorV3.rule.reference", { load, defaultValue: "ref. ≈ {{load}}" });
      })
    : undefined;
  // The set table's typing, with the notice when a cell leaves the rule.
  const ladderActions: V3ExerciseActions | undefined = edit && {
    ...edit.actions,
    round: (i, w) => {
      const r = edit.actions.round(i, w);
      return {
        ...r,
        updateRow: (rowIdx, patch) => {
          edit.progression.noticeEdit(i, w, rowIdx, patch);
          r.updateRow(rowIdx, patch);
        },
      };
    },
  };
  const name = resolveName(slot.exerciseId, slot.exerciseName);
  const tag = slot.group ? groupLabels(draft, sIdx, t).get(slot.group.id) : undefined;
  const variants = slot.variantsRaw
    .slice(1)
    .map((v) => resolveName(v.exerciseId, v.exerciseName))
    .join(", ");
  const oneSide = edit && canTrainOneSide(edit.tip.exerciseCatalog?.find((e) => e.slug === slot.exerciseId));

  return (
    <View style={styles.section}>
      <View style={styles.headRow}>
        {inPane ? null : (
          <IconButton
            theme={theme}
            name="chevron-left"
            accessibilityLabel={t("editorV3.backToDay", {
              day: dayLabel(draft, sIdx),
              defaultValue: "Back to {{day}}",
            })}
            onPress={nav.closeExercise}
          />
        )}
        <Text style={styles.headTitle} accessibilityRole="header" numberOfLines={2}>
          {name}
        </Text>
        {edit ? (
          <>
            <IconButton
              theme={theme}
              name="help"
              accessibilityLabel={t("editorV3.exerciseTipA11y", { name, defaultValue: "About {{name}}" })}
              onPress={() => setTipOpen(true)}
            />
            <IconButton
              theme={theme}
              name="more"
              accessibilityLabel={t("editorV3.exerciseMenuA11y", { name, defaultValue: "{{name}}, menu" })}
              onPress={() => setMenuOpen(true)}
            />
          </>
        ) : null}
      </View>
      {copyOpen && edit ? (
        <CopyToDaysTray
          theme={theme}
          draft={draft}
          sIdx={sIdx}
          what={name}
          onCopy={(days) => {
            edit.actions.copyToDays([slot.id], days);
            setCopyOpen(false);
          }}
          onCancel={() => setCopyOpen(false)}
        />
      ) : null}
      {tag !== undefined ? <Text style={styles.tag}>{tag}</Text> : null}
      {variants ? (
        <Text style={styles.muted}>
          {t("editorV3.variants", {
            names: variants,
            defaultValue: "Alternates with {{names}}. Not editable here yet.",
          })}
        </Text>
      ) : null}
      {noteOpen && edit ? (
        <PrescribeNoteEditor
          theme={theme}
          value={slot.instructions}
          onChange={(text) => edit.actions.setNote(slotIdx, text)}
          placeholder={t("composer.exerciseNotePlaceholder", {
            defaultValue: "Note for this exercise (shown while logging)",
          })}
          onClose={() => setNoteOpen(false)}
        />
      ) : slot.instructions.trim() ? (
        <Text style={styles.muted}>{slot.instructions}</Text>
      ) : null}
      {edit && (oneSide || slot.side !== undefined) ? (
        <SideChoiceRow theme={theme} side={slot.side} onChange={(side) => edit.actions.setSide(slotIdx, side)} />
      ) : !edit && slot.side !== undefined && slot.side !== "both" ? (
        <Text style={styles.muted}>
          {t("editorV3.sideRead", { side: sideWord[slot.side], defaultValue: "Side: {{side}}" })}
        </Text>
      ) : null}
      <RuleCard
        theme={theme}
        styles={styles}
        rec={rec}
        types={types}
        rounds={draft.cycleWeeks}
        words={words}
        onRealign={edit && (() => edit.progression.realign(slotIdx))}
        onChange={edit && (() => edit.onOpenRule([{ day: sIdx, slot: slotIdx }]))}
        lockedThrough={lockedThrough}
      />
      <RoundLadder
        theme={theme}
        styles={styles}
        slot={slot}
        slotIdx={slotIdx}
        cycleWeeks={draft.cycleWeeks}
        roundTypes={draft.roundTypes}
        expandedRound={nav.expandedRound}
        onToggleRound={edit && nav.toggleRound}
        lineOptions={lineOptions}
        cellUnit={cellUnit}
        actions={ladderActions}
        hand={hand}
        onRealignRound={edit && ((w) => edit.progression.realign(slotIdx, w))}
        refs={refs}
        lockedThrough={lockedThrough}
        onAddBlock={edit && draft.cycleWeeks + NEXT_BLOCK_ROUNDS <= MAX_CYCLE_WEEKS ? () => setBlockOpen(true) : undefined}
      />
      {edit ? (
        <ExerciseSheets
          theme={theme}
          styles={styles}
          draft={draft}
          sIdx={sIdx}
          slotIdx={slotIdx}
          name={name}
          openRound={nav.expandedRound}
          edit={edit}
          lineOptions={lineOptions}
          resolveName={resolveName}
          menuOpen={menuOpen}
          onCloseMenu={() => setMenuOpen(false)}
          tipOpen={tipOpen}
          onCloseTip={() => setTipOpen(false)}
          blockOpen={blockOpen}
          onCloseBlock={() => setBlockOpen(false)}
          onNote={() => setNoteOpen(true)}
          onCopyToDays={() => setCopyOpen(true)}
        />
      ) : null}
    </View>
  );
}
